/**
 * ManagedWindow — rendert EIN vom WindowManagerContext verwaltetes Fenster.
 *
 * Kombiniert zwei bestehende Bausteine zu dem Verhalten, das der globale
 * E-Mail-Composer bereits vormacht:
 *  - DraggableResizableWindow für Drag/Resize/Vollbild/Schließen.
 *  - TabContext.openVirtualTab, damit das Fenster einen eigenen Eintrag in
 *    der oberen Tab-Leiste bekommt.
 *
 * Zwei Ebenen des "Ausblendens":
 *  1. Tab-Wechsel (onDeactivate) → Fenster wird komplett ausgeblendet
 *     (auch keine Minimieren-Pille), da der Nutzer gerade woanders ist.
 *     Klick auf den Tab (onActivate) holt es zurück.
 *  2. Klick auf "Minimieren" im Fenster selbst → Fenster bleibt am
 *     aktiven Tab, wird aber durch eine kleine Pille ersetzt (wie beim
 *     E-Mail-Composer), aus der man es wieder aufklappen kann.
 *
 * In beiden Fällen bleibt die Komponente im DOM gemountet, ihr State geht
 * also nicht verloren.
 */
import { useEffect, useRef, useState } from 'react';
import * as Icons from 'lucide-react';
import DraggableResizableWindow from '../ui/DraggableResizableWindow';
import { useTabs } from '../../contexts/TabContext';

export default function ManagedWindow({ win, onClose }) {
  const { openVirtualTab, removeVirtualTab, updateVirtualTabTitle } = useTabs();
  const [hiddenByTab, setHiddenByTab] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    openVirtualTab({
      id: win.id,
      title: win.title,
      icon: win.icon,
      onActivate: () => setHiddenByTab(false),
      onDeactivate: () => setHiddenByTab(true),
      onClose: () => onCloseRef.current?.(),
    });
    return () => {
      try { removeVirtualTab(win.id); } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win.id]);

  // Erneuter Aufruf (z. B. andere Notiz im Notizbuch, props.initial.key ändert sich) holt ein
  // minimiertes Fenster zurück; reine Hintergrund-Aktualisierungen der Props tun das nicht.
  const aufrufKey = win.props?.initial?.key;
  useEffect(() => {
    if (aufrufKey) setMinimized(false);
  }, [aufrufKey]);

  useEffect(() => {
    updateVirtualTabTitle(win.id, win.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win.title]);

  const Content = win.component;
  const IconComp = Icons[win.icon] || Icons.FileText;

  // Bei Tab-Wechsel oder Minimieren nur ausblenden statt abbauen: Der Fensterinhalt
  // (z. B. Filter und Ergebnis der Batch-Sollstellung) bleibt so erhalten, bis das
  // Fenster wirklich geschlossen wird.
  return (
    <>
      {minimized && !hiddenByTab && (
        <button
          type="button"
          onClick={() => setMinimized(false)}
          className="fixed bottom-4 right-4 z-[90] flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-lg shadow-2xl border border-white/10 text-white hover:brightness-110 transition-all"
          style={{ background: '#1E3A5F' }}
          data-testid={`managed-window-restore-${win.id}`}
          title="Wieder öffnen"
        >
          <IconComp className="w-4 h-4" />
          <span className="text-sm font-semibold truncate max-w-[280px]">{win.title}</span>
          <span className="text-[11px] text-white/60 ml-1">wiederherstellen</span>
        </button>
      )}
      <div style={hiddenByTab || minimized ? { display: 'none' } : undefined}>
        <DraggableResizableWindow
          open
          active={!hiddenByTab && !minimized}
          onClose={onClose}
          onMinimize={() => setMinimized(true)}
          title={win.title}
          storageKey={win.storageKey}
          {...(win.windowProps || {})}
          testid={`managed-window-${win.id}`}
        >
          <Content {...win.props} onClose={onClose} />
        </DraggableResizableWindow>
      </div>
    </>
  );
}
